import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS, getLocalizedUrl, isSupportedLang } from "@/i18n/languages";

interface LanguageSwitcherProps {
  scrolled?: boolean;
}

export default function LanguageSwitcher({ scrolled = false }: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = isSupportedLang(i18n.language) ? i18n.language : "en";

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const selectLang = (lang: string) => {
    setOpen(false);
    if (lang !== current) {
      navigate(getLocalizedUrl(location.pathname, lang));
    }
  };

  const baseClass = scrolled
    ? "text-foreground-700 hover:text-foreground-950"
    : "text-white hover:text-white/90";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer ${baseClass}`}
      >
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-global-line text-base" />
        </span>
        <span>{t(`lang.${current}`)}</span>
        <span className="flex h-3 w-3 items-center justify-center">
          <i className={`ri-arrow-down-s-line text-sm transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-44 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1">
          {SUPPORTED_LANGS.map((lang) => (
            <button
              key={lang}
              type="button"
              onClick={() => selectLang(lang)}
              className={`flex w-full items-center justify-between whitespace-nowrap px-4 py-2 text-sm transition-colors cursor-pointer ${
                lang === current
                  ? "bg-primary-100 text-primary-800 font-semibold"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              <span>{t(`lang.${lang}`)}</span>
              {lang === current && (
                <span className="flex h-3 w-3 items-center justify-center">
                  <i className="ri-check-line text-sm" />
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}